import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { CertificateService } from '@app/services/certificates/certificate.service';
import { MultiCertService } from '@app/services/certificates/multi-cert.service';
import { LoadingService } from '@app/services/commons/Loading.service';
import { SessionService } from '@app/services/session/session.service';
import { UserService } from '@app/services/user.service';
import { environment } from '@env/environment';
import { UntilDestroy } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { CookieService } from 'ngx-cookie-service';
import { ToastrService } from 'ngx-toastr';
import { LocalStorageService } from 'ngx-webstorage';
import {
  CertificateActivationContext,
  shouldLogoutForEmptyCertificate,
} from '@app/helpers/certificate.helpers';
import { UserCertificate } from '@app/store/models/certificate.model';
import { UserSession } from '@app/store/models/session.model';
import { lastValueFrom } from 'rxjs';
import { I18nService } from 'src/app/translations/i18n';
import { OAuth2Service } from './../../../services/oauth2.service';

@UntilDestroy()
@Component({
  selector: 'app-oauth2',
  templateUrl: './oauth2.component.html',
  styleUrls: ['./oauth2.component.scss'],
  standalone: false,
})
export class Oauth2Component implements OnInit {
  constructor(
    private oAuth2Service: OAuth2Service,
    private sessionService: SessionService,
    private router: Router,
    private certificateService: CertificateService,
    private localStorageService: LocalStorageService,
    private multiCertService: MultiCertService,
    private toasterService: ToastrService,
    private translateService: TranslateService,
    private i18nService: I18nService,
    private loadingService: LoadingService,
    private cookieService: CookieService,
    private userService: UserService
  ) {}

  ngOnInit() {
    this.loadingService.show();
    // Ritarda la chiamata per attendere l'inizializzazione dei token
    setTimeout(() => {
      this.checkSession();
    }, 1000);
    setTimeout(() => {
      this.loadingService.hide();
    }, 4000);
  }

  async checkSession() {
    const idToken = await this.oAuth2Service.decodeToken(
      this.oAuth2Service.idToken
    );
    console.log('[OAUTH2] Id Token: ', idToken);

    const accessToken = this.oAuth2Service.accessToken;
    console.log('[OAUTH2] Access Token: ', accessToken);
    console.log('[OAUTH2] Access Token (new):', accessToken || 'NULL');
    // set cookie della response della chiamata /token, perché il valore attuale dell'access-token è legacy
    this.cookieService.set('oauth-access-token', accessToken, {
      path: '/',
      secure: true,
      sameSite: 'Strict',
    });
    // controllo se il valore del cookie corrisponda alla response di /token
    console.log(
      '[OAUTH2] Access Token (cookie): ' +
        this.cookieService.get('oauth-access-token')
    );

    // controllo se serve pulire la sessione dopo un rinnovo
    const clearSession = this.localStorageService.retrieve('clearSession');

    this.sessionService
      .getUserSession(clearSession)
      .then(async session => {
        console.log('🚀 ~ OAuth2Component ~ session', session);

        this.localStorageService.store('session', session);

        if (session.trissUser)
          this.localStorageService.store('isTrissUser', session.trissUser);

        let needActivation = [];
        if (this.localStorageService.retrieve('needActivation')) {
          // prendo la lista di certificati che hanno bisogno dell'attivazione
          needActivation = this.localStorageService.retrieve('needActivation');
        }

        if (session.needsForcePassword) {
          this.router.navigate(['/activation-steps']);
        } else {
          console.log('LoginComponent get certificates');

          const certificatePromise =
            this.certificateService.getCertificateList();

          await lastValueFrom(certificatePromise)
            .then(certificates => {
              if (
                !certificates ||
                certificates === null ||
                certificates?.length === 0 ||
                certificates === undefined
              ) {
                // OPEN NO CERTIFICATES MODAL
                this.showModal(null);
              }

              const signers = certificates.map(obj => obj.certId);
              this.multiCertService.setSigners(signers);

              this.certificateService.certID = certificates?.[0].certId;

              this.certificateService
                .getPromiseCertificate()
                .then(certificate => {
                  this.localStorageService.store('certificate', certificate);

                  const activationContext: CertificateActivationContext = {
                    needsForcePassword: session.needsForcePassword,
                    needActivationCertIds: needActivation,
                  };

                  this.routeAfterCertificateLoaded(
                    certificate,
                    certificates.length,
                    session,
                    needActivation,
                    activationContext
                  );
                })
                .catch(error => {
                  // CERTIFICATE ERROR
                  if (session.eseal) {
                    // Se la chiamata di certificato va in errore e sono eSeal allora portami all'attivazione
                    this.router.navigate(['/activation-steps']);
                  } else {
                    this.sessionService.logout();
                  }
                });
            })
            .catch(() => {
              // Errore API lista certificati (es. MS_NTF_003): logout, toast resta visibile
              void this.sessionService.logout();
            });
        }
      })
      .catch(error => {
        // SESSION ERROR
        console.error(error);

        // Ritarda la chiamata per attendere la valorizzazione della local storage
        setTimeout(() => {
          // show no cert modal if MS_NTF_004 error code
          if (
            this.localStorageService.retrieve('session-error-code') ===
            'MS_NTF_004'
          ) {
            this.showModal(null);
            this.localStorageService.clear('session-error-code');
          }
        }, 200);

        if (environment.production) {
          this.sessionService.logout();
        }
      });
    //se non ho lingue impostate nello storage prendo quella di isac altrimenti prendo quella impostata
    if (localStorage.getItem('current_language') === null) {
      if (idToken?.locale) {
        this.setLanguage(idToken.locale);
      }
    } else {
      this.setLanguage(localStorage.getItem('current_language'));
    }
  }

  setLanguage(language: string = 'it') {
    this.i18nService.language = language;
  }

  public noCertsModalVisible = false;

  /**
   * Logout su cert bianco solo prima di homepage/multi-cert, mai su activation-steps.
   */
  private routeAfterCertificateLoaded(
    certificate: UserCertificate,
    certificateCount: number,
    session: UserSession,
    needActivation: string[],
    activationContext: CertificateActivationContext
  ): void {
    if (certificateCount > 1) {
      if (shouldLogoutForEmptyCertificate(certificate, activationContext)) {
        void this.sessionService.logout();
        return;
      }

      this.router.navigate(['/multi-cert']);
      this.localStorageService.store('isMulticert', true);
      return;
    }

    if (
      needActivation.includes(this.certificateService.certID) ||
      certificate.certificateStatus === 'REGISTERED'
    ) {
      if (certificate.certificateStatus === 'EMITTED') {
        if (this.localStorageService.retrieve('needActivation')) {
          const filteredNeedActivation = this.localStorageService
            .retrieve('needActivation')
            .filter(elem => elem !== this.certificateService.certID);
          this.localStorageService.store(
            'needActivation',
            filteredNeedActivation
          );
        }
        if (session.icssUser || session.trissUser) {
          this.userService.observeSigns();
        }
        if (shouldLogoutForEmptyCertificate(certificate, activationContext)) {
          void this.sessionService.logout();
          return;
        }
        console.log('LoginComponent redirect HOME');
        this.router.navigate(['/homepage']);
      } else {
        this.router.navigate(['/activation-steps']);
      }
      return;
    }

    if (
      session.automatic &&
      !session.icssUser &&
      !session.trissUser &&
      !certificate.active
    ) {
      this.router.navigate(['/activation-steps']);
      return;
    }

    if (session.icssUser || session.trissUser) {
      this.userService.observeSigns();
    }

    this.cookieService.set('isac-app', 'legalcert_mysign', {
      path: '/',
      domain: '.infocert.it',
      sameSite: 'Strict',
      secure: true,
    });

    if (shouldLogoutForEmptyCertificate(certificate, activationContext)) {
      void this.sessionService.logout();
      return;
    }

    console.log('OAuth Component redirect HOME');
    this.router.navigate(['/homepage']);
  }

  showModal(vieRef: any) {
    console.log(vieRef);
    this.noCertsModalVisible = true;
  }

  onToggleNoCertsModal(visible: boolean): void {
    this.noCertsModalVisible = visible;
    if (!visible) {
      this.closeModal();
    }
  }

  closeModal() {
    // force logout for status modal if modal closed
    this.sessionService.logout();
    this.noCertsModalVisible = false;
  }

  openSupport() {
    window.open(
      this.translateService.instant('URLS.CONTACT_SUPPORT_URL'),
      '_blank'
    );
  }
}
